import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type MarketplaceKeyStatus = 'active' | 'redeemed' | 'revoked';

export interface MarketplaceKeyRecord {
  id: string;
  batchId: string;
  productId: string;
  keyValue: string;
  status: MarketplaceKeyStatus;
  redeemedBy: string | null;
  redeemedAt: string | null;
  createdAt: string;
}

export interface MarketplaceKeyBatchSummary {
  id: string;
  productId: string;
  quantity: number;
  createdAt: string;
  activeCount: number;
  redeemedCount: number;
  revokedCount: number;
  keys: MarketplaceKeyRecord[];
}

export function useMarketplaceKeys(productId: string | null, enabled = true) {
  const { session } = useAuth();
  const [batches, setBatches] = useState<MarketplaceKeyBatchSummary[]>([]);
  const [recentKeys, setRecentKeys] = useState<MarketplaceKeyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !productId) {
      setBatches([]);
      setRecentKeys([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const [{ data: batchRows, error: batchError }, { data: keyRows, error: keyError }] = await Promise.all([
      (supabase.from('marketplace_key_batches') as any)
        .select('id, product_id, quantity, created_at')
        .eq('product_id', productId)
        .order('created_at', { ascending: false }),
      (supabase.from('marketplace_keys') as any)
        .select('id, batch_id, product_id, key_value, status, redeemed_by, redeemed_at, created_at')
        .eq('product_id', productId)
        .order('created_at', { ascending: false }),
    ]);

    if (batchError || keyError) {
      setBatches([]);
      setRecentKeys([]);
      setError(batchError?.message || keyError?.message || 'Unable to load Maple Keys.');
      setLoading(false);
      return;
    }

    const normalizedKeys: MarketplaceKeyRecord[] = (keyRows ?? []).map((row: any) => ({
      id: row.id,
      batchId: row.batch_id,
      productId: row.product_id,
      keyValue: row.key_value,
      status: row.status,
      redeemedBy: row.redeemed_by,
      redeemedAt: row.redeemed_at,
      createdAt: row.created_at,
    }));

    const keysByBatch = new Map<string, MarketplaceKeyRecord[]>();
    normalizedKeys.forEach((key) => {
      const current = keysByBatch.get(key.batchId) ?? [];
      current.push(key);
      keysByBatch.set(key.batchId, current);
    });

    const nextBatches: MarketplaceKeyBatchSummary[] = (batchRows ?? []).map((row: any) => {
      const batchKeys = keysByBatch.get(row.id) ?? [];
      return {
        id: row.id,
        productId: row.product_id,
        quantity: row.quantity,
        createdAt: row.created_at,
        activeCount: batchKeys.filter((key) => key.status === 'active').length,
        redeemedCount: batchKeys.filter((key) => key.status === 'redeemed').length,
        revokedCount: batchKeys.filter((key) => key.status === 'revoked').length,
        keys: batchKeys,
      };
    });

    setBatches(nextBatches);
    setRecentKeys(normalizedKeys.slice(0, 20));
    setLoading(false);
  }, [enabled, productId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const summary = useMemo(() => {
    const totalGenerated = batches.reduce((total, batch) => total + batch.quantity, 0);
    const activeCount = batches.reduce((total, batch) => total + batch.activeCount, 0);
    const redeemedCount = batches.reduce((total, batch) => total + batch.redeemedCount, 0);
    const revokedCount = batches.reduce((total, batch) => total + batch.revokedCount, 0);

    return {
      totalGenerated,
      activeCount,
      redeemedCount,
      revokedCount,
    };
  }, [batches]);

  const generateBatch = useCallback(async (quantity: number) => {
    if (!productId) {
      throw new Error('Product is required.');
    }

    if (!session?.access_token) {
      throw new Error('You must be signed in to generate Maple Keys.');
    }

    setGenerating(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('generate-maple-keys', {
        body: { productId, quantity },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (invokeError || !data?.keys) {
        throw new Error(invokeError?.message || data?.error || 'Failed to generate Maple Keys.');
      }

      await refresh();
      return {
        batchId: data.batchId as string,
        createdAt: data.createdAt as string,
        quantity: data.quantity as number,
        keys: data.keys as string[],
      };
    } finally {
      setGenerating(false);
    }
  }, [productId, refresh, session?.access_token]);

  return {
    batches,
    recentKeys,
    summary,
    loading,
    generating,
    error,
    refresh,
    generateBatch,
  };
}
